<template>
  <div class="relative flex flex-col bg-white">
    <div
      v-if="!alwaysShow"
      ref="triggerEl"
      @click="handleOpen"
      class="flex h-10 cursor-pointer items-center justify-between rounded-sm bg-white"
      :class="
        showDropdown
          ? 'border-2 border-primary'
          : 'border hover:border-gray-300'
      "
      data-cy="calls-filter-button"
    >
      <div class="flex w-full items-center justify-between px-4">
        <div class="flex space-x-0.5">
          <div
            v-if="numberOfCallsRange.length === 2"
            class="date-picker-text flex items-center justify-between space-x-2"
          >
            <div>{{ $t("ACTIVITY.TABS.CALLS") }}:</div>
            <div class="date-picker-text">{{ numberOfCallsRange[0] }}</div>
            <div>-</div>
            <div class="date-picker-text">{{ numberOfCallsRange[1] }}</div>
          </div>
          <div v-if="dateRange.length && numberOfCallsRange.length">/</div>
          <div
            v-if="dateRange.length > 1"
            class="flex items-center justify-between"
          >
            <div class="date-picker-text">{{ dateRange[0] }}</div>
            <div v-if="dateRange.length === 2">-</div>
            <div class="date-picker-text">{{ dateRange[1] }}</div>
          </div>
          <div v-else-if="dateRange.length === 1" class="date-picker-text">
            {{ dynamicDateText }}
          </div>
          <div
            v-else-if="!dateRange.length && !numberOfCallsRange.length"
            class="text-sm"
          >
            {{ $t("SMART_FILTERS.SET_CALLS_FILTER") }}
          </div>
        </div>
        <span
          v-if="dateRange.length"
          @click.stop.prevent="handleClear"
          class="-mr-2"
        >
          <icon-x />
        </span>
      </div>
      <dropdown-chevron :is-open="showDropdown" class="text-500 -mr-2" />
    </div>
    <div
      v-if="showDropdown || alwaysShow"
      ref="teleportedCallFilter"
      v-on-click-outside="handleClose"
      class="z-100 max-h-[58vh] w-max overflow-y-auto bg-white shadow-xl"
      data-cy="filter-dropdown"
    >
      <div class="flex flex-col items-center justify-center border-b px-6 pt-6">
        <lf-h3 class="mb-4 self-start">
          {{ $t("SMART_FILTERS.NUMBER_OF_CALLS", 1) }}
        </lf-h3>
        <min-and-max
          :min="callsRange[0]"
          :max="callsRange[1]"
          :min-options="{
            hasError: callRangeError,
            ['data-maska']: '###',
          }"
          :max-options="{
            hasError: callRangeError,
            ['data-maska']: '###',
          }"
          @update:min="callsRange = [$event, callsRange[1]]"
          @update:max="callsRange = [callsRange[0], $event]"
        />
        <div
          v-if="callRangeError"
          class="pb-2 text-left text-xxs text-red-700 sm:text-xs"
        >
          {{ $t("COMMON.INVALID_RANGE") }}
        </div>
      </div>
      <inline-dynamic-date-picker
        v-model="date"
        date-format="MM/dd/yyyy"
        :show-select-buttons="false"
        :dynamic-date-options="dynamicDateOptions"
      />
      <hr />
      <div class="flex justify-center space-x-6 p-3">
        <lf-link :underlined="false" class="text-primary" @click="handleClear">
          {{ $t("COMMON.CLEAR_ALL") }}
        </lf-link>
        <lf-link :underlined="false" class="text-primary" @click="submit">
          {{ $t("COMMON.APPLY") }}
        </lf-link>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { type PropType, computed, ref, useTemplateRef, watch } from "vue";

import { isNullish } from "@/helpers/common";
import { useTeleportV2 } from "@/hooks/elements";

import InlineDynamicDatePicker from "@/components/ui/inputs/DatePicker/InlineDynamicDatePicker.vue";
import MinAndMax from "@/components/ui/inputs/MinAndMax.vue";

import type { CallsFilterPayload } from "@/models/applications";
import type { DynamicDateOption } from "@/models/datePicker";

const props = defineProps({
  numberOfCallsRange: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  dateRange: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  alwaysShow: {
    type: Boolean,
    default: false,
  },
  teleport: {
    type: Boolean,
    default: false,
  },
  dynamicDateOptions: {
    type: Array as PropType<DynamicDateOption[]>,
    default: () => [],
  },
});

const MAX_CALLS = 999;

const emit = defineEmits<{
  "update:numberOfCallsRange": [numberOfCallsRange: number[]];
  "update:dateRange": [dateRange: string[]];
  change: [filterPayload: CallsFilterPayload];
}>();

const triggerEl = useTemplateRef<HTMLDivElement>("triggerEl");
const teleportedCallFilter = useTemplateRef<HTMLDivElement>(
  "teleportedCallFilter"
);

const { width: triggerWidth } = useElementSize(triggerEl);
const { width: dropdownWidth } = useElementSize(teleportedCallFilter);

const showDropdown = ref(false);
const callRangeError = ref(false);

const date = ref<string[]>(props.dateRange);
const callsRange = ref<(number | null)[]>(props.numberOfCallsRange);

const centeringOffset = computed(() => ({
  left: (triggerWidth.value - dropdownWidth.value) / 2,
}));

const dynamicDateText = computed(
  () =>
    props.dynamicDateOptions.find(
      (option) => option.value === props.dateRange[0]
    )?.label ?? "-"
);

const handleClear = () => {
  callRangeError.value = false;
  date.value = [];
  callsRange.value = [];
  emit("update:numberOfCallsRange", []);
  emit("update:dateRange", []);
  emit("change", { number_of_calls: [], created_at_dates: [] });
  showDropdown.value = false;
};

const validCallsRange = () => {
  if (
    (isNullish(callsRange.value[0]) && callsRange.value[1]) ||
    (isNullish(callsRange.value[1]) && callsRange.value[0])
  ) {
    return false;
  }
  if (
    !isNullish(callsRange.value[0]) &&
    !isNullish(callsRange.value[1]) &&
    (callsRange.value[0] < 0 ||
      callsRange.value[1] < 0 ||
      callsRange.value[0] > callsRange.value[1] ||
      callsRange.value[1] > MAX_CALLS)
  ) {
    return false;
  }
  return true;
};

const submit = () => {
  callRangeError.value = !validCallsRange();
  if (callRangeError.value) {
    return;
  }
  const numberOfcalls =
    isNullish(callsRange.value[0]) || isNullish(callsRange.value[1])
      ? []
      : [callsRange.value[0], callsRange.value[1]];
  emit("update:numberOfCallsRange", numberOfcalls);
  emit("update:dateRange", date.value);
  emit("change", {
    number_of_calls: numberOfcalls,
    created_at_dates: date.value,
  });
  showDropdown.value = false;
};

const handleOpen = () => {
  if (showDropdown.value) {
    return;
  }
  showDropdown.value = true;
};

const handleClose = () => {
  if (!showDropdown.value) {
    return;
  }
  showDropdown.value = false;
};

watch(
  () => props.dateRange,
  (newVal) => {
    date.value = newVal;
  }
);

defineExpose({
  showDropdown,
});

if (props.teleport) {
  useTeleportV2({
    target: teleportedCallFilter,
    to: "#menus",
    offset: centeringOffset,
    checkForOverflow: true,
  });
}
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.date-picker-text {
  @apply text-sm text-gray-600;
}
</style>
